import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Edit, AlertTriangle } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { usePaymentPlanQuery, useMarkInstallmentPaid } from '@/lib/builder/hooks/usePaymentPlan';
import { IInstallment } from '@/lib/builder/models/payment-plan.model';
import {
    toCents,
    rebalanceToTotal,
    isPaidIn,
    paidInstallmentsById,
} from '@/lib/builder/utils/payment-plan.utils';
import { PaymentPlanEditorModal, toInstallmentDraft } from './PaymentPlanEditorModal';
import { PaymentPlanSummary } from './PaymentPlanSummary';
import { InstallmentStatusBadge } from './InstallmentStatusBadge';
import { PlatformFeeDueBadge } from '@/lib/payments/components/PlatformFeeDueBadge';
import { MarkInstallmentPaidModal } from './MarkInstallmentPaidModal';

interface PaymentPlanCardProps {
    itineraryId?: string;
    itineraryTotal: number;
    /** Data di partenza: usata per proporre la scadenza del saldo. */
    departureDate?: string;
    /**
     * Chi puo' registrare un incasso a mano. Dal 02/09/2026 sono tutti quelli che passano
     * `can_manage_itinerary()` — TD assegnato, agenzia, admin — e non piu' il solo admin.
     * Resta una scrittura contabile che nessuno verifica contro Stripe, ma ora lascia una
     * commissione a debito con sopra il nome di chi l'ha dichiarata: e' quello, non il
     * permesso, a tenerla onesta. Il guard vero sta in `mark_installment_paid`; qui si
     * nasconde solo cio' che non funzionerebbe.
     */
    canMarkPaid: boolean;
    formatAmount: (value: number) => string;
    formatDate: (iso: string) => string;
}

export const PaymentPlanCard = ({
    itineraryId,
    itineraryTotal,
    departureDate,
    canMarkPaid,
    formatAmount,
    formatDate,
}: PaymentPlanCardProps) => {
    const { t } = useI18n();
    const { data: plan, isLoading } = usePaymentPlanQuery(itineraryId);
    const markPaid = useMarkInstallmentPaid(itineraryId);

    /** null = editor chiuso. 'rebalance' apre con gli importi gia' riproporzionati. */
    const [editorIntent, setEditorIntent] = useState<'edit' | 'rebalance' | null>(null);
    /** Rata per cui è aperta la conferma di incasso manuale. */
    const [installmentToMark, setInstallmentToMark] = useState<IInstallment | null>(null);

    if (!itineraryId) {
        return (
            <Card className="w-full">
                <CardContent className="p-4">
                    <p className="text-sm text-gray-500">{t('checkout.paymentPlan.saveItineraryFirst')}</p>
                </CardContent>
            </Card>
        );
    }

    const mismatch = plan ? toCents(plan.total_amount) !== toCents(itineraryTotal) : false;

    /**
     * Il totale e' cambiato dopo che il piano e' stato definito: le rate non pagate vanno
     * riportate sul nuovo importo prima di qualsiasi altra modifica. L'editor si apre gia'
     * con gli importi ricalcolati e non lascia salvare finche' non quadrano, quindi non
     * serve distinguere fra "modifica" e "ribilancia": con un disallineamento aperto c'e'
     * una sola cosa da fare.
     */
    const editorIntentForOpen: 'edit' | 'rebalance' = mismatch ? 'rebalance' : 'edit';

    /**
     * Il ribilanciamento sull'ultima rata non basta quando il nuovo totale e' piu' basso di
     * quanto gia' incassato piu' le altre rate: in quel caso l'editor riparte da una
     * ripartizione in parti uguali sulle rate aperte, e se non c'e' nulla di aperto resta
     * solo la modifica a mano.
     */
    const canAutoRebalance =
        plan &&
        rebalanceToTotal(
            plan.installments.map(toInstallmentDraft),
            toCents(itineraryTotal),
            isPaidIn(paidInstallmentsById(plan.installments))
        ) !== null;

    return (
        <Card className="w-full">
            <CardContent className="p-4 flex flex-col gap-3">
                {!plan ? (
                    /* Senza piano non c'e' riepilogo su cui appoggiare il pulsante:
                       resta sulla riga del messaggio. */
                    <div className="flex items-center gap-3">
                        <p className="text-sm text-gray-500 flex-1">{t('checkout.paymentPlan.empty')}</p>
                        <Button type="button" size="sm" onClick={() => setEditorIntent('edit')} disabled={isLoading}>
                            <Edit className="w-3.5 h-3.5 mr-1.5" />
                            {t('checkout.paymentPlan.actions.create')}
                        </Button>
                    </div>
                ) : (
                    <>
                        {/* Il totale e' cambiato dopo che il piano e' stato definito. Non si
                            riallinea da solo: le rate potrebbero essere gia' state comunicate,
                            e quelle incassate non si toccano affatto. Ma non si puo' nemmeno
                            lasciar correre — il cliente pagherebbe una cifra che non e' piu'
                            il prezzo del viaggio — quindi l'avviso e' rosso e chiede un'azione,
                            non un'occhiata. */}
                        {mismatch && (
                            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                                <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                <div className="flex flex-col gap-1 text-xs">
                                    <span className="font-medium text-red-900">
                                        {t('checkout.paymentPlan.outOfSyncRequired', {
                                            planned: formatAmount(plan.total_amount),
                                            total: formatAmount(itineraryTotal),
                                        })}
                                    </span>
                                    <span className="text-red-800">
                                        {canAutoRebalance
                                            ? t('checkout.paymentPlan.outOfSyncHint')
                                            : t('checkout.paymentPlan.outOfSyncManualHint')}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setEditorIntent('rebalance')}
                                        className="text-left text-red-800 underline w-fit"
                                    >
                                        {t('checkout.paymentPlan.actions.rebalance')}
                                    </button>
                                </div>
                            </div>
                        )}

                        <PaymentPlanSummary
                            plan={plan}
                            formatAmount={formatAmount}
                            action={
                                <Button
                                    type="button"
                                    size="sm"
                                    className="h-8"
                                    onClick={() => setEditorIntent(editorIntentForOpen)}
                                    disabled={isLoading}
                                >
                                    <Edit className="w-3.5 h-3.5 mr-1.5" />
                                    {t('common.edit')}
                                </Button>
                            }
                        />

                        {/* Nessun tetto all'altezza: la card vive in una colonna che scorre
                            già per intero, e uno scroll dentro l'altro si incastrerebbe. */}
                        <div className="flex flex-col divide-y divide-gray-100">
                            {plan.installments.map((installment) => (
                                <div key={installment.id} className="flex items-center gap-2.5 py-1.5">
                                    <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-gray-300 text-[10px] font-semibold text-gray-500 tabular-nums">
                                        {installment.status === 'paid' ? '✓' : installment.seq}
                                    </span>

                                    <div className="flex flex-1 flex-col min-w-0 leading-tight">
                                        <div className="flex items-center gap-1.5 min-w-0">
                                            <span className="text-sm font-medium text-gray-900 truncate">
                                                {installment.label}
                                            </span>
                                            <InstallmentStatusBadge
                                                status={installment.status}
                                                isOverdue={installment.is_overdue}
                                                isProcessing={installment.payment_processing}
                                            />
                                            {/* Accanto allo stato, non al posto suo: la rata
                                                e' incassata davvero, quello che manca e' la
                                                nostra fee. */}
                                            <PlatformFeeDueBadge amount={installment.platform_fee_due} />
                                        </div>
                                        <span className="text-[11px] text-gray-500">
                                            {installment.status === 'paid' && installment.paid_at
                                                ? t('checkout.paymentPlan.paidOn', {
                                                      date: formatDate(installment.paid_at),
                                                  })
                                                : t('checkout.paymentPlan.dueOn', {
                                                      date: formatDate(installment.due_date),
                                                  })}
                                        </span>
                                    </div>

                                    {/* Importo e azione sulla stessa riga: impilarli faceva crescere
                                        ogni rata di una terza riga, e con molte rate si vedeva solo
                                        l'inizio dell'elenco. */}
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                        <div className="flex flex-col items-end leading-tight">
                                            <span className="text-sm font-semibold text-gray-900 tabular-nums">
                                                {formatAmount(installment.amount)}
                                            </span>
                                            <span className="text-[11px] text-gray-400 tabular-nums">
                                                {t('checkout.paymentPlan.percentOfTotal', {
                                                    percent: Number(installment.percentage ?? 0).toFixed(2),
                                                })}
                                            </span>
                                        </div>
                                        {/* Incasso fuori piattaforma: bonifico, contanti. Il pagamento
                                            con carta lo registra il webhook Stripe da solo.

                                            Disabilitato con un addebito differito in volo: registrare
                                            l'incasso a mano mentre il bonifico e' in viaggio e' un
                                            doppio incasso. Il TA aspetta l'esito. Stessa regola
                                            della dashboard /payments. */}
                                        {canMarkPaid && installment.status === 'pending' && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 px-1.5 text-[11px] text-gray-500 hover:text-gray-900"
                                                disabled={installment.payment_processing}
                                                title={
                                                    installment.payment_processing
                                                        ? t('payments.actions.processingHint')
                                                        : undefined
                                                }
                                                onClick={() => setInstallmentToMark(installment)}
                                            >
                                                {t('checkout.paymentPlan.actions.markPaid')}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </CardContent>

            <PaymentPlanEditorModal
                isOpen={editorIntent !== null}
                itineraryId={itineraryId}
                itineraryTotal={itineraryTotal}
                departureDate={departureDate}
                intent={editorIntent ?? 'edit'}
                formatAmount={formatAmount}
                onClose={() => setEditorIntent(null)}
            />

            {/* Montata solo per chi puo' usarla: senza il pulsante non si aprirebbe
                comunque, ma cosi' il flusso non esiste proprio per gli altri ruoli. */}
            {canMarkPaid && (
            <MarkInstallmentPaidModal
                installment={installmentToMark}
                isOpen={Boolean(installmentToMark)}
                isSaving={markPaid.isPending}
                formatAmount={formatAmount}
                formatDate={formatDate}
                onClose={() => setInstallmentToMark(null)}
                onConfirm={({ installmentId, paymentMethod }) =>
                    markPaid.mutate(
                        { installmentId, paymentMethod },
                        { onSuccess: () => setInstallmentToMark(null) },
                    )
                }
            />
            )}
        </Card>
    );
};
